"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  BarChart,
  DensityChart,
} from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import {
  type ProbabilityRow,
  ProbabilityRows,
} from "@/components/tools/stats-distributions-more/probability-rows";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  barGeometry,
  densityGeometry,
} from "@/lib/tool-kit/stats-distributions/chart";
import {
  discreteProbabilities,
  distributionTable,
  MAX_TABLE_ROWS,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  discreteUniformModel,
  uniformBetween,
  uniformCdf,
  uniformMoments,
  uniformPdf,
  uniformQuantile,
  uniformSurvival,
} from "@/lib/tool-kit/stats-distributions-more/uniform";

/** English strings. A translated content file overrides these through `ui`. */
export const UNIFORM_DISTRIBUTION_CALCULATOR_STRINGS = {
  kindLabel: "Type of uniform distribution",
  kindContinuous: "Continuous — any value from a to b",
  kindDiscrete: "Discrete — whole numbers from a to b",
  aLabel: "Lower end (a)",
  bLabel: "Upper end (b)",
  xLabel: "Value (x)",
  x2Label: "Upper end of a range (optional)",
  x2Hint: "Adds P(x ≤ X ≤ this value) to the results.",
  pLabel: "Percentile: probability p (optional)",
  pHint: "Finds the x with P(X ≤ x) = p, such as 0.9 for the 90th percentile.",
  kLabel: "Value (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds P(k ≤ X ≤ this value) to the results.",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  density: "Density at {x}: f(x) = 1/(b − a). A density, not a probability.",
  atMostX: "at most {x} (also: less than {x})",
  moreThanX: "more than {x} (also: at least {x})",
  between: "from {a} to {b}, inclusive",
  equal: "exactly {k}",
  atMost: "at most {k}",
  lessThan: "fewer than {k}",
  atLeast: "at least {k}",
  greaterThan: "more than {k}",
  mean: "Mean ((a + b) / 2)",
  variance: "Variance ((b − a)² / 12)",
  varianceDiscrete: "Variance ((n² − 1) / 12)",
  sd: "Standard deviation",
  median: "Median",
  workingHeading: "How this was calculated",
  densityLine: "f(x) = 1 / (b − a) = 1 / ({b} − {a}) = {f}",
  cdfLine: "P(X ≤ x) = (x − a) / (b − a) = ({x} − {a}) / ({b} − {a}) = {p}",
  quantileLine: "x = a + p(b − a) = {a} + {p} × ({b} − {a}) = {x}",
  countLine: "n = b − a + 1 = {b} − {a} + 1 = {n} equally likely values",
  pmfLine: "P(X = k) = 1/n = 1/{n} = {p}",
  chartHeading: "Density curve",
  chartLabel:
    "Flat density of a continuous uniform distribution with the area for your probability shaded",
  chartCaption:
    "Uniform density on [{a}, {b}], height {f}. The shaded area is {shaded}.",
  shadedAtMost: "P(X ≤ x)",
  shadedBetween: "P({a} ≤ X ≤ {b})",
  barHeading: "Probability distribution",
  barLabel:
    "Bar chart of the equal probabilities for each whole number from {from} to {to}, with the bars your question covers drawn darker",
  tableCaption: "Discrete uniform probabilities for k = {from} to {to}",
  tableLimit:
    "The table and chart are drawn while the distribution has at most 200 values.",
  enterBounds: "Enter a lower end a and an upper end b that is greater than a.",
  enterWholeBounds:
    "Enter whole numbers for a and b, with b greater than a and the spread at most 1,000,000,000.",
  enterX: "Enter x as a number.",
  enterX2: "Enter the upper end of the range as a number.",
  enterP: "Enter p as a number from 0 to 1.",
  enterK: "Enter k as a whole number.",
  enterK2: "Enter the upper end of the range as a whole number.",
} as const;

type Strings = typeof UNIFORM_DISTRIBUTION_CALCULATOR_STRINGS;
type Kind = "continuous" | "discrete";

const MAX_DISCRETE_SPAN = 1_000_000_000;

interface Form {
  a: string;
  b: string;
  x: string;
  x2: string;
  p: string;
}

const INITIAL: Record<Kind, Form> = {
  continuous: { a: "0", b: "20", x: "8", x2: "14", p: "0.9" },
  discrete: { a: "1", b: "6", x: "5", x2: "", p: "" },
};

interface Setup {
  a: number;
  b: number;
  x: number;
  x2: number | null;
  p: number | null;
}

function readOptional(
  text: string,
  parse: (raw: string) => number | null
): { given: boolean; value: number | null } {
  if (text.trim() === "") {
    return { given: false, value: null };
  }
  return { given: true, value: parse(text) };
}

function readSetup(kind: Kind, form: Form): Setup | keyof Strings {
  const whole = kind === "discrete";
  const parse = whole ? parseIntegerInput : parseNumberInput;
  const a = parse(form.a);
  const b = parse(form.b);
  const badBounds = whole ? "enterWholeBounds" : "enterBounds";
  if (a === null || b === null || b <= a) {
    return badBounds;
  }
  if (whole && b - a > MAX_DISCRETE_SPAN) {
    return badBounds;
  }
  const x = parse(form.x);
  if (x === null) {
    return whole ? "enterK" : "enterX";
  }
  const x2 = readOptional(form.x2, parse);
  if (x2.given && x2.value === null) {
    return whole ? "enterK2" : "enterX2";
  }
  const p = whole
    ? { given: false, value: null }
    : readOptional(form.p, parseNumberInput);
  if (p.given && (p.value === null || p.value < 0 || p.value > 1)) {
    return "enterP";
  }
  return { a, b, x, x2: x2.value, p: p.value };
}

function rowsFor(
  setup: Setup,
  strings: Strings,
  sig: (value: number) => string
): ProbabilityRow[] {
  const { a, b, x, x2 } = setup;
  const xText = sig(x);
  const rows: ProbabilityRow[] = [
    {
      notation: `P(X ≤ ${xText})`,
      meaning: t(strings.atMostX, { x: xText }),
      value: uniformCdf(x, a, b),
    },
    {
      notation: `P(X > ${xText})`,
      meaning: t(strings.moreThanX, { x: xText }),
      value: uniformSurvival(x, a, b),
    },
  ];
  if (x2 !== null) {
    const low = sig(Math.min(x, x2));
    const high = sig(Math.max(x, x2));
    rows.push({
      notation: `P(${low} ≤ X ≤ ${high})`,
      meaning: t(strings.between, { a: low, b: high }),
      value: uniformBetween(x, x2, a, b),
    });
  }
  rows.push({
    notation: `f(${xText})`,
    meaning: t(strings.density, { x: xText }),
    value: uniformPdf(x, a, b),
    kind: "density",
  });
  return rows;
}

function ContinuousSketch({
  setup,
  strings,
  sig,
  fmt,
}: {
  setup: Setup;
  strings: Strings;
  sig: (value: number) => string;
  fmt: NumberFormatter;
}) {
  const { a, b, x, x2 } = setup;
  const low = x2 === null ? a : Math.min(x, x2);
  const high = x2 === null ? x : Math.max(x, x2);
  const geometry = useMemo(() => {
    const pad = (b - a) * 0.2;
    return densityGeometry({
      pdf: (value) => uniformPdf(value, a, b),
      xMin: a - pad,
      xMax: b + pad,
      shaded: [[Math.max(a, low), Math.min(b, high)]],
      markers: x2 === null ? [x] : [low, high],
      samples: 480,
    });
  }, [a, b, x, x2, low, high]);
  const shaded =
    x2 === null
      ? strings.shadedAtMost
      : t(strings.shadedBetween, { a: sig(low), b: sig(high) });
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <DensityChart
        format={(value) => fmt(value, { maximumSignificantDigits: 4 })}
        geometry={geometry}
        label={strings.chartLabel}
      />
      <p className="text-muted-foreground text-sm">
        {t(strings.chartCaption, {
          a: sig(a),
          b: sig(b),
          f: sig(1 / (b - a)),
          shaded,
        })}
      </p>
    </div>
  );
}

function ContinuousResults({
  setup,
  strings,
  fmt,
}: {
  setup: Setup;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { a, b, x, p } = setup;
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const moments = uniformMoments(a, b);
  const span = { a: sig(a), b: sig(b) };
  return (
    <>
      <ProbabilityRows
        fmt={fmt}
        labels={strings}
        rows={rowsFor(setup, strings, sig)}
      />
      <StatGrid>
        <StatCard label={strings.mean} value={sig(moments.mean)} />
        <StatCard label={strings.variance} value={sig(moments.variance)} />
        <StatCard label={strings.sd} value={sig(moments.sd)} />
      </StatGrid>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm">
          {t(strings.densityLine, { ...span, f: sig(1 / (b - a)) })}
        </p>
        <p className="font-mono text-muted-foreground text-sm">
          {t(strings.cdfLine, {
            ...span,
            x: sig(x),
            p: sig(uniformCdf(x, a, b)),
          })}
        </p>
        {p === null ? null : (
          <p className="font-mono text-muted-foreground text-sm">
            {t(strings.quantileLine, {
              ...span,
              p: sig(p),
              x: sig(uniformQuantile(p, a, b)),
            })}
          </p>
        )}
      </div>
      <ContinuousSketch fmt={fmt} setup={setup} sig={sig} strings={strings} />
    </>
  );
}

function DiscreteResults({
  setup,
  strings,
  fmt,
}: {
  setup: Setup;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { a, b, x, x2 } = setup;
  const n = b - a + 1;
  const model = useMemo(() => discreteUniformModel(a, b), [a, b]);
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const shown = n <= MAX_TABLE_ROWS;
  const rows = useMemo(
    () => (shown ? distributionTable(model, a, b) : []),
    [shown, model, a, b]
  );
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  const low = x2 === null ? x : Math.min(x, x2);
  const high = x2 === null ? x : Math.max(x, x2);
  const highlighted = (value: number) => value >= low && value <= high;
  const span = { from: fmt(a), to: fmt(b) };
  return (
    <>
      <ProbabilitySummary
        fmt={fmt}
        k={x}
        labels={strings}
        probabilities={discreteProbabilities(model, x, x2)}
      />
      <StatGrid>
        <StatCard label={strings.mean} value={sig(model.mean)} />
        <StatCard
          label={strings.varianceDiscrete}
          value={sig(model.variance)}
        />
        <StatCard label={strings.sd} value={sig(Math.sqrt(model.variance))} />
      </StatGrid>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm">
          {t(strings.countLine, { a: fmt(a), b: fmt(b), n: fmt(n) })}
        </p>
        <p className="font-mono text-muted-foreground text-sm">
          {t(strings.pmfLine, { n: fmt(n), p: four(1 / n) })}
        </p>
      </div>
      {shown ? (
        <>
          <div className={`${CARD} space-y-3`}>
            <h3 className={WIDGET_H3}>{strings.barHeading}</h3>
            <BarChart
              format={(value) => fmt(value)}
              geometry={geometry}
              highlighted={highlighted}
              label={t(strings.barLabel, span)}
            />
          </div>
          <DistributionTable
            caption={t(strings.tableCaption, span)}
            fmt={fmt}
            highlighted={highlighted}
            rows={rows}
          />
        </>
      ) : (
        <Notice>{strings.tableLimit}</Notice>
      )}
    </>
  );
}

export default function UniformDistributionCalculatorWidget() {
  const strings = useToolUi(UNIFORM_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [kind, setKind] = useState<Kind>("continuous");
  const [forms, setForms] = useState(INITIAL);
  const form = forms[kind];
  const update = (patch: Partial<Form>) =>
    setForms((current) => ({
      ...current,
      [kind]: { ...current[kind], ...patch },
    }));
  const setup = readSetup(kind, form);
  const discrete = kind === "discrete";

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.kindLabel}
          name="ud-kind"
          onChange={setKind}
          options={[
            { value: "continuous", label: strings.kindContinuous },
            { value: "discrete", label: strings.kindDiscrete },
          ]}
          value={kind}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ud-a"
            label={strings.aLabel}
            numeric
            onChange={(a) => update({ a })}
            value={form.a}
          />
          <TextField
            id="ud-b"
            label={strings.bLabel}
            numeric
            onChange={(b) => update({ b })}
            value={form.b}
          />
          <TextField
            id="ud-x"
            label={discrete ? strings.kLabel : strings.xLabel}
            numeric
            onChange={(x) => update({ x })}
            value={form.x}
          />
          <TextField
            hint={discrete ? strings.k2Hint : strings.x2Hint}
            id="ud-x2"
            label={discrete ? strings.k2Label : strings.x2Label}
            numeric
            onChange={(x2) => update({ x2 })}
            value={form.x2}
          />
          {discrete ? null : (
            <TextField
              hint={strings.pHint}
              id="ud-p"
              label={strings.pLabel}
              numeric
              onChange={(p) => update({ p })}
              value={form.p}
            />
          )}
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof setup === "string" ? <Notice>{strings[setup]}</Notice> : null}
        {typeof setup === "object" && discrete ? (
          <DiscreteResults fmt={fmt} setup={setup} strings={strings} />
        ) : null}
        {typeof setup === "object" && !discrete ? (
          <ContinuousResults fmt={fmt} setup={setup} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
